import React, { lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import '../styles/Collection.css';
import squareBackground from '../images/square.png';
import { Container } from 'react-bootstrap';
import Breadcrumbs from './Breadcrumbs';
import usePageNav from '../hooks/usePageNav';
import getNavigationInitialState from '../initialState/navigation';
import SearchPageSidebar from './SearchPageSidebar';
import SearchPageProductList from './SearchPageProductList';
import PageNavCurPageIndicator from './PageNavCurPageIndicator';
import PageNav from './PageNav';
import { LoadingContext } from './context/LoadingContext';
import usePrefetch from '../hooks/usePrefetch';
import Seo from './Seo';
import { bool, object } from 'prop-types';
import useSearch from '../hooks/useSearch';
import NotFound from './NotFound';
import hasStoreName from '../helpers/hasStoreName';
import AppContext from './context/AppContext';
import SearchPageSidebarSkeleton from './SearchPageSidebarSkeleton';
import ChildCategories from './ChildCategories';
import ChildCategoriesSkeleton from './ChildCategoriesSkeleton';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';
import ResponsiveImage from './ResponsiveImage';
import LazyLoadWhenInView from './LazyLoadWhenInView';
import CategoryList from './CategoryList';
import HomeBrands from './HomeBrands';
import { ToastContext } from './context/ToastContext';

const CategoryDescription = lazy(() => import('./CategoryDescription'));

/** Config */
const DESCRIPTION_HEIGHT_LIMIT = 300;
const DESCRIPTION_HEIGHT_SET_DELAY = 300;
/** Helpers */
const toProperCase = (str) => str.at(0).toUpperCase() + str.substring(1).toLowerCase();
const getValidatedTagPrefix = (tagPrefix) => tagPrefix
    .split('-')
    .map((p) => p.split(' '))
    .reduce((prev, curr) => prev.concat(curr), [])
    .filter((p) => p.trim() !== '') // filter out empty strings
    .map((p) => toProperCase(p))
    .join(' ');

// Collection fetching func.
const fetchCollectionData = async (handle = '') => {
    const response = await fetch(new URL(`/api/collections/handle/${handle}`, process.env.APP_URL));
    const data = await response.json();
    return data?.collection ?? null;
};

export default function Category({ isMobile, searchBoxRef }) {
    const { appContext } = useContext(AppContext);
    const { sendMessage } = useContext(ToastContext);
    const appConfig = appContext.config.themeConfig;
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const collectionBasePath = collectionPath[0].pathBeforeParams;
    const { handle, tagFilter } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { searchQuery, updateSearchParams } = useSearch();
    const [searchLoading, setSearchLoading] = useState(true);
    const [loadingHierarchy, setLoadingHierarchy] = useState(true);
    const [productList, setProductList] = useState([]);
    const [featuredProductList, setFeaturedProductList] = useState([]);
    const [hierarchy, setHierarchy] = useState({});
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);
    const [originalDescriptionHeight, setOriginalDescriptionHeight] = useState(null);
    const [topBanner, setTopBanner] = useState(null);
    const [collectionBody, setCollectionBody] = useState(null);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    /* Pagination code */
    const containerRef = useRef(null);
    const [pagesData, setPagesData] = useState(getNavigationInitialState());
    const handlePageChange = (newCurPage) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', parseInt(newCurPage));
        setSearchParams(newSearchParams);
    };
    const {
        curPage,
        curRange,
        nextRange,
        generatedPagesData,
        paginateUser,
    } = usePageNav({
        containerRef,
        pagesData,
        initialCurPage: Math.min(parseInt(searchParams.get('page') || 0, 0)),
        onUpdate: handlePageChange,
    });
    /* End pagination code */
    const {
        isLoaded: collectionLoaded,
        data: collectionData,
    } = usePrefetch({
        key: 'collection',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            startLoading();
            const data = await fetchCollectionData(handle);
            setSearchLoading(false);
            finishLoading();
            return data;
        }
    });

    const getValidatedTagFilter = useCallback(() => {
        const validatedTagFilter = [];
        if (tagFilter && tagFilter.indexOf('_') !== -1) {
            // Split tag prefix string before and after the first underscore
            const tagPrefix = tagFilter.slice(0, tagFilter.indexOf('_')).trim();
            const tagValue = tagFilter.slice(tagFilter.indexOf('_') + 1).trim();
            if (tagPrefix && tagValue) {
                // Split tag prefix provided in the URL by the hyphens
                // Capitalize first letter of each segment
                // Join with spaces
                const tagPrefixValidated = getValidatedTagPrefix(tagPrefix);
                // Add tag value as-is
                // Add tag value lowercase
                // Add tag value capitalized first letter
                const tagValues = new Set([
                    tagValue,
                    tagValue.toLowerCase(),
                    tagValue.toUpperCase(),
                    toProperCase(tagValue),
                ]);
                [...tagValues.values()].forEach((val) => {
                    validatedTagFilter.push(tagPrefixValidated, val);
                });
            }
        }
        const tags = searchParams.getAll('t');
        tags.forEach((tag) => {
            if (tag.indexOf(',') !== -1) {
                const tagPrefix = tag.slice(0, tags.indexOf(',')).trim();
                const tagValue = tag.slice(tags.indexOf(',') + 1).trim();
                if (tagPrefix && tagValue) {
                    const tagPrefixValidated = getValidatedTagPrefix(tagPrefix);
                    const tagValues = new Set([
                        tagValue,
                        tagValue.toLowerCase(),
                        tagValue.toUpperCase(),
                        toProperCase(tagValue),
                    ]);
                    [...tagValues.values()].forEach((val) => {
                        validatedTagFilter.push(tagPrefixValidated, val);
                    });
                }
            }
        });

        return validatedTagFilter;
    }, [tagFilter, searchParams]);

    // Product fetching func.
    /* Search functionality */
    const handleSearch = useCallback(async () => {
        try {
            if (!collectionLoaded) {
                return;
            }
            if (isNaN(collectionData.id) || collectionData.id === null) {
                return;
            }

            setSearchLoading(true);

            const validatedTagFilter = getValidatedTagFilter();
            const params = new URLSearchParams({
                ...searchQuery,
                c: [collectionData.id],
                ...validatedTagFilter && ({ t: validatedTagFilter }),
                fields: constants.MINIMAL_PRODUCT_FIELDS.join(','),
            }).toString();
            // Products
            const productResponse = await fetch(new URL('api/products/filter?' + params, process.env.APP_URL));
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                    productMaxPrice: productData.pageInfo.productMaxPrice,
                });
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setSearchLoading(false);
        }
    }, [searchQuery, collectionData, collectionLoaded, setSearchLoading, getValidatedTagFilter, setPagesData, setProductList]);
    // Get featured products
    const fetchFeaturedProducts = useCallback(async () => {
        try {
            if (isNaN(collectionData.id) || collectionData.id === null) {
                return;
            }
            const response = await fetch(new URL(`/api/collections/${collectionData.id}/products/featured`, process.env.APP_URL));
            const data = await response.json();
            setFeaturedProductList(data.products?.map((product) => ({ ...product,
                isFeatured: true })) ?? []);
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, [collectionData, setFeaturedProductList]);

    useEffect(() => {
        if (collectionLoaded
            && collectionData) {
            fetchFeaturedProducts();
        }
    }, [collectionLoaded, collectionData]);
    /* End search functionality */

    // Fetch hierarchy
    const fetchHierarchy = useCallback(async () => {
        try {
            const response = await fetch(new URL(`/api/collections/${collectionData.id}/hierarchy`, process.env.APP_URL));
            const data = await response.json();
            setHierarchy(data.hierarchy ?? {});
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoadingHierarchy(false);
        }
    }, [collectionData, setLoadingHierarchy, setHierarchy]);

    useEffect(() => {
        if (collectionLoaded
            && collectionData) {
            fetchHierarchy();
        }
    }, [collectionLoaded, collectionData]);
    // End fetch hierarchy
    // Fetch products on load and update
    useEffect(() => {
        if (collectionLoaded
            && collectionData) {
            handleSearch();
        }
    }, [searchQuery, collectionLoaded, collectionData, getValidatedTagFilter]);
    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!collectionLoaded
            || !collectionData) {
            return '';
        }
        let title = collectionData.seoTitle ?? collectionData.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [collectionLoaded, collectionData]);

    const collectionPageRef = useCallback(() => {
        searchBoxRef.current?.enableAlterPath();
    }, [searchBoxRef]);

    const scrollToProductListHandler = () => {
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleReadMoreClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isDescriptionExpanded === false) {
            collectionBody.style.maxHeight = 'none';
            setIsDescriptionExpanded(true);
        } else {
            // handle browsers that can't handle scrolling and updating dom at the same time
            setTimeout(scrollToProductListHandler, constants.IMMEDIATE_TIMEOUT_MS);
            collectionBody.style.maxHeight = `${DESCRIPTION_HEIGHT_LIMIT}px`;
            setIsDescriptionExpanded(false);
        }
    }, [ isDescriptionExpanded, collectionBody, setIsDescriptionExpanded, scrollToProductListHandler]);

    const fetchTopBanner = useCallback(async () => {
        if (isNaN(parseInt(collectionData?.id))) {
            return;
        }
        try {
            const placement = appConfig.imagePlacements.collection.topBanner;
            const url = new URL(
                `/api/collections/${collectionData.id}/image/placement/${placement}`,
                process.env.APP_URL,
            );
            const response = await fetch(url);
            const data = await response.json();
            setTopBanner(data ?? null);
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, [collectionData, setTopBanner]);

    // Set original description height and content max height, if necessary.
    useEffect(() => {
        if (!collectionBody) {
            return;
        }

        setTimeout(() => {
            const height = collectionBody.scrollHeight || collectionBody.getBoundingClientRect().height;

            collectionBody.style.maxHeight = 'none';

            setOriginalDescriptionHeight(height);

            if (height > DESCRIPTION_HEIGHT_LIMIT) {
                collectionBody.style.maxHeight = `${DESCRIPTION_HEIGHT_LIMIT}px`;
                setIsDescriptionExpanded(false);
            } else {
                setIsDescriptionExpanded(true);
            }
        }, DESCRIPTION_HEIGHT_SET_DELAY);
    }, [collectionBody, collectionData]);

    // Fetch top banner image.
    useEffect(() => {
        fetchTopBanner();
    }, [fetchTopBanner]);

    if (collectionLoaded === true && !collectionData) {
        return (<NotFound />);
    }

    return (<Container
        style={{ padding:'0 0 30px' }}
        ref={collectionPageRef}
        fluid
    >
        {(collectionLoaded === true && collectionData !== null) && (<>
            {Object.keys(collectionData).length > 0 && (<>
                <Seo
                    seoTitle={title}
                    seoDescription={collectionData.seoDescription}
                    indexed={collectionData.isIndexed}
                    canonicalUrl={new URL(`${collectionBasePath}/${collectionData.handle}`, process.env.APP_URL).toString()}
                    image={collectionData.imageUrl ?? null}
                    breadsArr={[
                        {
                            title: 'Home',
                            handle: '/',
                        },
                        {
                            title: 'Collections',
                            handle: `${collectionBasePath}/`,
                        },
                        {
                            title: collectionData.title,
                            handle: `${collectionBasePath}/${collectionData.handle}`,
                        },
                    ]}
                />
                <div
                    className={'collection--heading'}
                >
                    <Breadcrumbs
                        breadcrumbList={collectionData.breadcrumbs?.map(({ title, handle }) => ([
                            title,
                            `${collectionBasePath}/${handle}`,
                        ])) ?? [[collectionData.title, `${collectionBasePath}/${handle}` ]]} />
                    {topBanner?.imagePath && <ResponsiveImage
                        imageUrl={`${process.env.RESOURCE_ATTACHMENTS_URL}/${topBanner.imagePath}`}
                        imageAlt={topBanner.altText} />}
                    {(isMobile && collectionData?.childCollectionsCount > 0) && (
                        <h6
                            className={'child-categories-headline'}
                        >
                            To find your parts, please choose a sub-category:
                        </h6>
                    )}
                    {(loadingHierarchy && collectionData?.childCollectionsCount > 0) ? (
                        <ChildCategoriesSkeleton
                            childCollectionsCount={collectionData.childCollectionsCount}
                        />
                    ): (
                        <ChildCategories
                            hierarchy={hierarchy}
                        />
                    )}
                </div>
                <div
                    className={'category--sidebar-container'}
                >
                    <div
                        className={'category--aside-content'}
                    >
                        <CategoryList />
                    </div>
                    <div
                        className={'category-main-content'}
                    >
                        <h1
                            className={'collection--title'}
                            style={{
                                backgroundImage: `url(${squareBackground})`
                            }}
                        >
                            {collectionData.title}
                        </h1>
                        <LazyLoadWhenInView>
                            <CategoryDescription
                                ref={setCollectionBody}
                                descriptionHtml={collectionData.descriptionHtml}
                            />
                        </LazyLoadWhenInView>

                        {/* Only show fade overlay and button when content exceeds height limit */}
                        {originalDescriptionHeight > DESCRIPTION_HEIGHT_LIMIT && (
                            <>
                                <div
                                    className={`collection--fade-overlay ${isDescriptionExpanded ? 'd-none' : 'd-block'}`}
                                ></div>
                                <div
                                    className={'text-center'}
                                >
                                    <button
                                        onClick={handleReadMoreClick}
                                        className={'read-more-button'}
                                    >
                                        {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                                    </button>
                                </div>
                            </>
                        )}

                        <div
                            className={'search-page--inner'}
                        >
                            {searchLoading ? (
                                <SearchPageSidebarSkeleton />
                            ) : (
                                <SearchPageSidebar
                                    searchQuery={searchQuery}
                                    setSearchQuery={updateSearchParams}
                                    vendorList={pagesData.availableVendors ?? []}
                                    tagPrefixes={pagesData.availableTagPrefixes ?? []}
                                    tagList={pagesData.availableTagFilters ?? []}
                                    productMaxPrice={pagesData.productMaxPrice}
                                    searchLoading={searchLoading}
                                    productCount={productList.length}
                                />
                            )}
                            <SearchPageProductList
                                ref={containerRef}
                                isMobile={isMobile}
                                productList={[...featuredProductList, ...productList]}
                                searchLoading={searchLoading}
                                collectionHandle={handle}
                                searchQuery={{
                                    ...searchQuery,
                                    c: [collectionData.id],
                                    ...getValidatedTagFilter() && ({ t: getValidatedTagFilter() }),
                                }}
                                setProductList={setProductList}
                                totalPages={pagesData.pages}
                                curPage={curPage}
                                paginateUser={paginateUser}
                            >
                                {productList.length > 0 ? (
                                    <>
                                        <PageNavCurPageIndicator
                                            curRange={curRange}
                                            nextRange={nextRange}
                                            totalCount={pagesData.count}
                                            resourceType={'products'}
                                            noStyle={true}
                                        />
                                        <PageNav
                                            curPage={curPage}
                                            totalPages={pagesData.pages}
                                            generatedPagesData={generatedPagesData}
                                            paginateUser={paginateUser}
                                        />
                                    </>
                                ): null}
                            </SearchPageProductList>
                        </div>
                    </div>
                    <div
                        className={'category--brands-content'}
                    >
                        <HomeBrands />
                    </div>
                </div>
            </>)}
        </>)}
    </Container>);
}

Category.propTypes = {
    isMobile: bool,
    searchBoxRef: object,
};
